import React, { createContext, useContext, useReducer, useEffect } from 'react';
import authService from '../services/authService';
import { clearAuthData } from '../utils/storage';

// Auth states
const AUTH_STATES = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
};

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Initial state
const initialState = {
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
  authState: AUTH_STATES.LOADING,
};

/**
 * Auth reducer
 * - Handles auth lifecycle actions (loading, login success/failure, logout, updates).
 * - Pure reducer used by useReducer in `AuthProvider`.
 */
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        loading: false,
        error: null,
        authState: AUTH_STATES.AUTHENTICATED,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: action.payload.error,
        authState: AUTH_STATES.UNAUTHENTICATED,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
        authState: AUTH_STATES.UNAUTHENTICATED,
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: action.payload.user,
        error: null,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Create auth context
const AuthContext = createContext(null);

// Create auth provider component
/**
 * AuthProvider
 * - Wraps app and provides auth state and actions via context.
 * - Initializes local auth status on mount and exposes login/register/logout helpers.
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

        // First check if user is authenticated locally (without API call)
        const authStatus = authService.checkAuthStatus();
        
        if (authStatus.isAuthenticated && authStatus.user) {
          // Token is valid and we have user data, set authenticated state
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: { user: authStatus.user },
          });
        } else {
          // Not authenticated or token invalid
          clearAuthData();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuthData();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };
    initializeAuth();
  }, []);

  /**
   * Login function
   * - Calls authService.login and dispatches success/failure actions.
   */
  const login = async (identifier, password, rememberMe = false) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

      const result = await authService.login(identifier, password, rememberMe);

      if (result.success) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: result.user },
        });
        return { success: true, user: result.user };
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: result.error },
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred during login';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage },
      });
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Register function
   * - Registers a new user via authService and sets auth state on success.
   */
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

      const result = await authService.register(userData);

      if (result.success) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: result.user },
        });
        return { success: true, user: result.user };
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: result.error },
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred during registration';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage },
      });
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Logout function
   * - Calls authService.logout and clears local auth state.
   */
  const logout = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

      await authService.logout();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Still dispatch logout to clear local state
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      return { success: true };
    }
  };

  /**
   * Update user data from server
   */
  const updateUser = async () => {
    try {
      const result = await authService.getCurrentUser();

      if (result.success) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_USER,
          payload: { user: result.user },
        });
        return { success: true, user: result.user };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = 'Failed to update user information';
      return { success: false, error: errorMessage };
    }
  };

  /** Update user password via auth service */
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      const result = await authService.updatePassword(currentPassword, newPassword);
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to update password' };
    }
  };

  /** Request a password reset email */
  const requestPasswordReset = async (identifier) => {
    try {
      const result = await authService.requestPasswordReset(identifier);
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to send password reset email' };
    }
  };

  /** Reset password using token */
  const resetPassword = async (token, newPassword) => {
    try {
      const result = await authService.resetPassword(token, newPassword);
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to reset password' };
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const value = {
    // State
    ...state,
    
    // Actions
    login,
    register,
    logout,
    updateUser,
    updatePassword,
    requestPasswordReset,
    resetPassword,
    clearError,
    
    // Utility functions
    isLoading: state.loading,
    isLoggedIn: state.isAuthenticated,
    currentUser: state.user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  
  return context;
};

// Export auth states and actions for external use
export { AUTH_STATES, AUTH_ACTIONS };

export default AuthContext;
